/**
 * Agent Action Atoms - Write operations for AI Agents
 *
 * These atoms handle all agent mutations (create, update, delete, publish).
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { atom } from "jotai";
import {
  AgentSchema,
  AgentListItemSchema,
  AgentGroupItemSchema,
  type CreateAgentBody,
  type UpdateAgentBody,
} from "@/isomorphic/schemas";
import {
  _apiWorkspaceAgentsAtom,
  workspaceAgentsStatusAtom,
  workspaceAgentsErrorAtom,
  currentAgentAtom,
  currentAgentStatusAtom,
} from "../core/agents";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidated, fetchValidatedArray, postValidated, putValidated } from "@/frontend/lib/validation";

// ============================================================================
// FETCH ACTIONS
// ============================================================================

/**
 * Fetch all agents for a workspace (with validation)
 */
export const fetchWorkspaceAgentsAtom = atom(null, async (get, set, args: { jwt: string; workspaceId: string }) => {
  const { jwt, workspaceId } = args;

  if (!jwt || !workspaceId) {
    return;
  }

  set(workspaceAgentsStatusAtom, "loading");
  set(workspaceAgentsErrorAtom, null);

  const result = await fetchValidatedArray(AgentListItemSchema, API_ROUTES.agents.getByWorkspace(workspaceId), {
    jwt,
    context: "Workspace Agents",
  });

  if (result.success) {
    set(_apiWorkspaceAgentsAtom, result.data);
    set(workspaceAgentsStatusAtom, "success");
  } else {
    console.error("[AgentActions] Error fetching workspace agents:", result.error);
    set(workspaceAgentsStatusAtom, "error");
    set(workspaceAgentsErrorAtom, result.error);
  }
});

/**
 * Fetch agent groups for a workspace (with aggregated deployment info)
 * This eliminates N+1 queries by including all deployed versions in a single query
 */
export const fetchWorkspaceAgentGroupsAtom = atom(
  null,
  async (get, set, args: { jwt: string; workspaceId: string }) => {
    const { jwt, workspaceId } = args;

    if (!jwt || !workspaceId) {
      return;
    }

    set(workspaceAgentsStatusAtom, "loading");
    set(workspaceAgentsErrorAtom, null);

    const result = await fetchValidatedArray(
      AgentGroupItemSchema,
      API_ROUTES.agents.getGroupsByWorkspace(workspaceId),
      {
        jwt,
        context: "Workspace Agent Groups",
      },
    );

    if (result.success) {
      set(_apiWorkspaceAgentsAtom, result.data);
      set(workspaceAgentsStatusAtom, "success");
    } else {
      console.error("[AgentActions] Error fetching workspace agent groups:", result.error);
      set(workspaceAgentsStatusAtom, "error");
      set(workspaceAgentsErrorAtom, result.error);
    }
  },
);

/**
 * Fetch a single agent by ID (with validation)
 */
export const fetchAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return null;
  }

  set(currentAgentStatusAtom, "loading");

  const result = await fetchValidated(AgentSchema, API_ROUTES.agents.getById(agentId), {
    jwt,
    context: `Agent ${agentId}`,
  });

  if (result.success) {
    set(currentAgentAtom, result.data);
    set(currentAgentStatusAtom, "success");
    return result.data;
  } else {
    console.error("[AgentActions] Error fetching agent:", result.error);
    set(currentAgentStatusAtom, "error");
    return null;
  }
});

// ============================================================================
// MUTATION ACTIONS
// ============================================================================

/**
 * Create a new agent (with validation)
 */
export const createAgentAtom = atom(null, async (get, set, args: { jwt: string; payload: CreateAgentBody }) => {
  const { jwt, payload } = args;

  if (!jwt) {
    return null;
  }

  const result = await postValidated(AgentSchema, API_ROUTES.agents.create, payload, { jwt, context: "Create Agent" });

  if (result.success) {
    // Update local state
    const currentAgents = get(_apiWorkspaceAgentsAtom);
    set(_apiWorkspaceAgentsAtom, [...currentAgents, result.data]);
    return result.data;
  } else {
    console.error("[AgentActions] Error creating agent:", result.error);
    throw new Error(result.error);
  }
});

/**
 * Update an existing agent (with validation)
 */
export const updateAgentAtom = atom(
  null,
  async (get, set, args: { jwt: string; agentId: string; payload: Partial<UpdateAgentBody> }) => {
    const { jwt, agentId, payload } = args;

    if (!jwt || !agentId) {
      return null;
    }

    const result = await putValidated(AgentSchema, API_ROUTES.agents.update(agentId), payload, {
      jwt,
      context: `Update Agent ${agentId}`,
    });

    if (result.success) {
      // Update local state
      const currentAgents = get(_apiWorkspaceAgentsAtom);
      set(
        _apiWorkspaceAgentsAtom,
        currentAgents.map((a) => (a.id === agentId ? result.data : a)),
      );

      // Update current agent if it's the one being edited
      const currentAgent = get(currentAgentAtom);
      if (currentAgent?.id === agentId) {
        set(currentAgentAtom, result.data);
      }

      return result.data;
    } else {
      console.error("[AgentActions] Error updating agent:", result.error);
      throw new Error(result.error);
    }
  },
);

/**
 * Delete an agent (with validation)
 */
export const deleteAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return false;
  }

  try {
    const response = await fetch(API_ROUTES.agents.delete(agentId), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const { error } = await response.json();

    if (error) {
      throw new Error(error || "Failed to delete agent");
    }

    // Update local state
    const currentAgents = get(_apiWorkspaceAgentsAtom);
    set(
      _apiWorkspaceAgentsAtom,
      currentAgents.filter((a) => a.id !== agentId),
    );

    return true;
  } catch (error) {
    console.error("[AgentActions] Error deleting agent:", error);
    return false;
  }
});

/**
 * Release an agent (draft → released)
 * Creates an immutable snapshot version
 */
export const releaseAgentAtom = atom(
  null,
  async (get, set, args: { jwt: string; agentId: string; releaseNote?: string }) => {
    const { jwt, agentId, releaseNote } = args;

    if (!jwt || !agentId) {
      return { success: false, error: "Missing parameters" };
    }

    const result = await postValidated(
      AgentSchema,
      API_ROUTES.agents.release(agentId),
      { releaseNote: releaseNote || null },
      { jwt, context: `Release Agent ${agentId}` },
    );

    if (result.success) {
      const workspaceId = result.data.workspaceId;
      if (workspaceId) {
        await set(fetchWorkspaceAgentGroupsAtom, { jwt, workspaceId });
      }
      return { success: true, data: result.data };
    } else {
      return { success: false, error: result.error };
    }
  },
);

/**
 * Deploy an agent to Retell (released → deployed)
 * Creates Retell voice agents for all phases
 */
export const deployAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return { success: false, error: "Missing parameters" };
  }

  const result = await postValidated(
    AgentSchema,
    API_ROUTES.agents.deploy(agentId),
    {},
    { jwt, context: `Deploy Agent ${agentId}` },
  );

  if (result.success) {
    const workspaceId = result.data.workspaceId;
    if (workspaceId) {
      await set(fetchWorkspaceAgentGroupsAtom, { jwt, workspaceId });
    }
    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
});

/**
 * Activate an agent version (deployed → active)
 * Makes this version the production version for new follow-ups
 */
export const activateAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return { success: false, error: "Missing parameters" };
  }

  const result = await postValidated(
    AgentSchema,
    API_ROUTES.agents.activate(agentId),
    {},
    { jwt, context: `Activate Agent ${agentId}` },
  );

  if (result.success) {
    const workspaceId = result.data.workspaceId;
    if (workspaceId) {
      await set(fetchWorkspaceAgentGroupsAtom, { jwt, workspaceId });
    }
    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
});

/**
 * Deactivate an agent version (active → deployed)
 * Removes this version from production (no new follow-ups possible)
 */
export const deactivateAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return { success: false, error: "Missing parameters" };
  }

  const result = await postValidated(
    AgentSchema,
    API_ROUTES.agents.deactivate(agentId),
    {},
    { jwt, context: `Deactivate Agent ${agentId}` },
  );

  if (result.success) {
    const workspaceId = result.data.workspaceId;
    if (workspaceId) {
      await set(fetchWorkspaceAgentGroupsAtom, { jwt, workspaceId });
    }
    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
});

/**
 * Redeploy an agent (for backward compatibility)
 * @deprecated Use releaseAgentAtom + deployAgentAtom instead
 */
export const redeployAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return { success: false, error: "Missing parameters" };
  }

  const route = API_ROUTES.agents.redeploy(agentId);
  const context = `Redeploy Agent ${agentId}`;

  const result = await postValidated(AgentSchema, route, {}, { jwt, context });

  if (result.success) {
    const workspaceId = result.data.workspaceId;
    if (workspaceId) {
      await set(fetchWorkspaceAgentGroupsAtom, { jwt, workspaceId });
    }
    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
});

/**
 * Archive an agent (with validation)
 * Updates the agent in the local state with archivedAt timestamp
 */
export const archiveAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return { success: false, error: "Missing parameters" };
  }

  const result = await postValidated(
    AgentSchema,
    API_ROUTES.agents.archive(agentId),
    {},
    { jwt, context: `Archive Agent ${agentId}` },
  );

  if (result.success) {
    // Update local state optimistically
    const currentAgents = get(_apiWorkspaceAgentsAtom);
    set(
      _apiWorkspaceAgentsAtom,
      currentAgents.map((a) => (a.id === agentId ? result.data : a)),
    );

    // Update current agent if it's the one being archived
    const currentAgent = get(currentAgentAtom);
    if (currentAgent?.id === agentId) {
      set(currentAgentAtom, result.data);
    }

    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
});

/**
 * Restore an archived agent (with validation)
 * Updates the agent in the local state by removing archivedAt timestamp
 */
export const restoreAgentAtom = atom(null, async (get, set, args: { jwt: string; agentId: string }) => {
  const { jwt, agentId } = args;

  if (!jwt || !agentId) {
    return { success: false, error: "Missing parameters" };
  }

  const result = await postValidated(
    AgentSchema,
    API_ROUTES.agents.restore(agentId),
    {},
    { jwt, context: `Restore Agent ${agentId}` },
  );

  if (result.success) {
    // Update local state optimistically
    const currentAgents = get(_apiWorkspaceAgentsAtom);
    set(
      _apiWorkspaceAgentsAtom,
      currentAgents.map((a) => (a.id === agentId ? result.data : a)),
    );

    // Update current agent if it's the one being restored
    const currentAgent = get(currentAgentAtom);
    if (currentAgent?.id === agentId) {
      set(currentAgentAtom, result.data);
    }

    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
});
